import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { optionsFromCatalog } from '../domain/action-options';
import {
  ActionOptionsView,
  CloseoutApprovalView,
  CloseoutDecisionRequest,
  CloseoutRejectionView,
  CloseoutSubmissionView,
  ControlledPlanRequest,
  InsertionPlans,
  IsoDate,
  PlanChangePreview,
  PlanCommandReceipt,
  PlanErrorCode,
  PlanExecutionReceipt,
  PlanExecutionView,
  PlanHistoryView,
  PlanItemView,
  PlanMergePreview,
  PlanMergeReceipt,
  PlanView,
  RbaRunView,
  RecordExecutionRequest,
  ReplaceActionItemsRequest,
  AreaActionChangeRequest,
  AreaActionChangeView,
  ReplacementPreviewView,
  ReplacementReceipt,
  SubmitCloseoutRequest,
  ThreeDMismatchView
} from './plan-management.models';
import { PlanNameCatalogService } from './plan-name-catalog.service';

/** A refused Plan Management request, carrying the server's code rather than an HTTP status. */
export class PlanApiError extends Error {
  constructor(
    readonly code: PlanErrorCode,
    message: string,
    readonly status: number,
    readonly currentRevision: number | null = null
  ) {
    super(message);
    this.name = 'PlanApiError';
  }

  get isRevisionConflict(): boolean {
    return this.code === 'PLAN_REVISION_CONFLICT';
  }
}

/**
 * Plan Management v2 (`api/v2/plans`). This is the only plan source for the screen:
 * the legacy `api/InspectionPlan/*` routes are answered by a compatibility shim on
 * cut-over assets and must not be read from here.
 */
@Injectable({ providedIn: 'root' })
export class PlanManagementApiService {
  private readonly http = inject(HttpClient);
  private readonly names = inject(PlanNameCatalogService);
  private readonly base = `${environment.API_INSPEC}v2/plans`;
  private readonly v2 = `${environment.API_INSPEC}v2`;

  getPlans(assetId: string): Observable<PlanView[]> {
    return this.http
      .get<PlanView[]>(this.base, { params: { assetId } })
      .pipe(catchError(toPlanApiError));
  }

  /** Per region, the plans a new inspection of the type would join on the day. */
  getInsertionPlans(assetId: string, regionIds: readonly string[], inspectionTypeId: string, day: IsoDate): Observable<InsertionPlans[]> {
    return this.http
      .get<InsertionPlans[]>(`${this.base}/insertion-plans`, {
        params: { assetId, regionIds: regionIds.join(','), inspectionTypeId, date: day }
      })
      .pipe(catchError(toPlanApiError));
  }

  getPlan(planId: string): Observable<PlanView> {
    return this.http.get<PlanView>(`${this.base}/${enc(planId)}`).pipe(catchError(toPlanApiError));
  }

  /** A plan's own points holding a locked follow-up row among its own occurrences (its 3D info). */
  getLockedFollowUpCount(planId: string): Observable<number> {
    return this.http.get<number>(`${this.base}/${enc(planId)}/locked-follow-ups`).pipe(catchError(toPlanApiError));
  }

  getItems(planId: string): Observable<PlanItemView[]> {
    return this.http
      .get<PlanItemView[]>(`${this.base}/${enc(planId)}/items`)
      .pipe(catchError(toPlanApiError));
  }

  getHistory(planId: string): Observable<PlanHistoryView[]> {
    return this.http
      .get<PlanHistoryView[]>(`${this.base}/${enc(planId)}/history`)
      .pipe(catchError(toPlanApiError));
  }

  getExecutions(planId: string): Observable<PlanExecutionView[]> {
    return this.http
      .get<PlanExecutionView[]>(`${this.base}/${enc(planId)}/executions`)
      .pipe(catchError(toPlanApiError));
  }

  previewReschedule(planId: string, request: ControlledPlanRequest): Observable<PlanChangePreview> {
    return this.post<PlanChangePreview>(`${enc(planId)}/reschedule/preview`, request);
  }

  reschedule(planId: string, request: ControlledPlanRequest): Observable<PlanCommandReceipt> {
    return this.post<PlanCommandReceipt>(`${enc(planId)}/reschedule`, request);
  }

  previewMerge(planId: string, request: ControlledPlanRequest): Observable<PlanMergePreview> {
    return this.post<PlanMergePreview>(`${enc(planId)}/merge/preview`, request);
  }

  merge(planId: string, request: ControlledPlanRequest): Observable<PlanMergeReceipt> {
    return this.post<PlanMergeReceipt>(`${enc(planId)}/merge`, request);
  }

  previewRecurrence(planId: string, request: ControlledPlanRequest): Observable<PlanChangePreview> {
    return this.post<PlanChangePreview>(`${enc(planId)}/recurrence/preview`, request);
  }

  changeRecurrence(planId: string, request: ControlledPlanRequest): Observable<PlanCommandReceipt> {
    return this.post<PlanCommandReceipt>(`${enc(planId)}/recurrence`, request);
  }

  cancel(planId: string, request: ControlledPlanRequest): Observable<PlanCommandReceipt> {
    return this.post<PlanCommandReceipt>(`${enc(planId)}/cancel`, request);
  }

  /**
   * What the plan could change action to, with the AI assessment when there is one.
   * The endpoint is proposed, not served yet: while the route does not exist (a bare
   * 404/405, no domain code) the asset's action catalog stands in, unscored.
   */
  getActionOptions(plan: PlanView): Observable<ActionOptionsView> {
    return this.http.get<ActionOptionsView>(`${this.base}/${enc(plan.PlanId)}/action-options`).pipe(
      catchError(toPlanApiError),
      catchError((error: unknown) => {
        const missingRoute = error instanceof PlanApiError && (error.code === 'HTTP_404' || error.code === 'HTTP_405');
        if (!missingRoute) return throwError(() => error);
        return this.names.actionsFor(plan.AssetId).pipe(map(names => optionsFromCatalog(plan, names)));
      })
    );
  }

  /** One Critical Area action and what it can change to; the server resolves its plan. */
  getAreaActionChange(actionOccurrenceId: string): Observable<AreaActionChangeView> {
    return this.http
      .get<AreaActionChangeView>(`${this.v2}/critical-area-actions/${enc(actionOccurrenceId)}`)
      .pipe(catchError(toPlanApiError));
  }

  previewAreaActionChange(actionOccurrenceId: string, request: AreaActionChangeRequest): Observable<ReplacementPreviewView> {
    return this.http
      .post<ReplacementPreviewView>(`${this.v2}/critical-area-actions/${enc(actionOccurrenceId)}/change/preview`, request)
      .pipe(catchError(toPlanApiError));
  }

  changeAreaAction(actionOccurrenceId: string, request: AreaActionChangeRequest): Observable<ReplacementReceipt> {
    return this.http
      .post<ReplacementReceipt>(`${this.v2}/critical-area-actions/${enc(actionOccurrenceId)}/change`, request)
      .pipe(catchError(toPlanApiError));
  }

  previewReplacement(planId: string, request: ReplaceActionItemsRequest): Observable<ReplacementPreviewView> {
    return this.post<ReplacementPreviewView>(`${enc(planId)}/replace-action-items/preview`, request);
  }

  replaceActionItems(planId: string, request: ReplaceActionItemsRequest): Observable<ReplacementReceipt> {
    return this.post<ReplacementReceipt>(`${enc(planId)}/replace-action-items`, request);
  }

  recordExecution(
    planId: string,
    planItemId: string,
    request: RecordExecutionRequest
  ): Observable<PlanExecutionReceipt> {
    return this.post<PlanExecutionReceipt>(`${enc(planId)}/items/${enc(planItemId)}/execution`, request);
  }

  /** Outlined repairs of the plan that ThreeD reports as executed. Read-only. */
  getThreeDMismatches(planId: string): Observable<ThreeDMismatchView[]> {
    return this.http
      .get<ThreeDMismatchView[]>(`${this.base}/${enc(planId)}/threed-mismatches`)
      .pipe(catchError(toPlanApiError));
  }

  submitCloseout(request: SubmitCloseoutRequest): Observable<CloseoutSubmissionView> {
    return this.http
      .post<CloseoutSubmissionView>(`${this.v2}/closeout-submissions`, request)
      .pipe(catchError(toPlanApiError));
  }

  getCloseoutSubmission(submissionId: string): Observable<CloseoutSubmissionView> {
    return this.http
      .get<CloseoutSubmissionView>(`${this.v2}/closeout-submissions/${enc(submissionId)}`)
      .pipe(catchError(toPlanApiError));
  }

  approveCloseout(submissionId: string, request: CloseoutDecisionRequest): Observable<CloseoutApprovalView> {
    return this.http
      .post<CloseoutApprovalView>(`${this.v2}/closeout-submissions/${enc(submissionId)}/approve`, request)
      .pipe(catchError(toPlanApiError));
  }

  rejectCloseout(submissionId: string, request: CloseoutDecisionRequest): Observable<CloseoutRejectionView> {
    return this.http
      .post<CloseoutRejectionView>(`${this.v2}/closeout-submissions/${enc(submissionId)}/reject`, request)
      .pipe(catchError(toPlanApiError));
  }

  getRbaRun(rbaRunId: string): Observable<RbaRunView> {
    return this.http.get<RbaRunView>(`${this.v2}/rba-runs/${enc(rbaRunId)}`).pipe(catchError(toPlanApiError));
  }

  private post<T>(path: string, body: unknown): Observable<T> {
    return this.http.post<T>(`${this.base}/${path}`, body).pipe(catchError(toPlanApiError));
  }
}

function enc(id: string): string {
  return encodeURIComponent(id);
}

/**
 * The controller answers refusals as ProblemDetails with the domain code in `code`
 * (and `currentRevision` on conflicts). Newtonsoft's ProblemDetails converter writes
 * the standard members in camelCase.
 */
function toPlanApiError(error: unknown): Observable<never> {
  if (!(error instanceof HttpErrorResponse)) {
    return throwError(() => error);
  }

  const body = (error.error ?? {}) as Record<string, unknown>;
  const code = str(body['code']) ?? str(body['title']) ?? (error.status === 0 ? 'NETWORK_ERROR' : `HTTP_${error.status}`);
  const message =
    str(body['detail']) ??
    (error.status === 0 ? 'The Inspection service could not be reached.' : error.message);
  const revision = typeof body['currentRevision'] === 'number' ? (body['currentRevision'] as number) : null;

  return throwError(() => new PlanApiError(code, message, error.status, revision));
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}
